/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Schema, ValidationError } from '@squiz/component-editing-ui';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  ErrorMessage,
  SPINNER_TYPE_REGULAR,
  SelectField,
  SelectFieldOption,
  SelectFieldProps,
  Spinner,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { JsonEditor } from 'apps/console-core/src/Components/JsonEditor/JsonEditor';
import { JsonEditorProps } from 'apps/console-core/src/Components/JsonEditor/JsonEditor.props';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EntityJobManifest,
  JobRunnerServiceBeginExecutionDto,
  getJobRunnerService,
  useJobContexts,
  useJobs,
} from '../../../../Services/job-runner';
import { JOB_EXECUTIONS_DATA_IDS } from '../../JobExecutions.constants';
import { START_JOB_EXECUTIONS_DATA_IDS } from './StartJobForm.constants';
import { StartJobFormProps } from './StartJobForm.props';
import { JobManifestInputSchema, StartJobFormDataType } from './StartJobForm.types';

/**
 * Populates the content in the StartJob Dialog box.
 */
export const StartJobForm = (props: StartJobFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { setAfterClose, close, heading, refreshData, setButtonKeys, setButtons } = props;

  const { updateActiveToast } = useToastStore();

  // Job Runner service
  const jobRunnerService = getJobRunnerService();

  const [contextList] = useJobContexts();
  const [jobs] = useJobs();

  // States
  const [selectedContext, setSelectedContext] = useState<string>();
  const [jobManifest, setJobManifest] = useState<EntityJobManifest | null>(null);

  const [contextError, setContextError] = useState<string>(``);
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);

  const [validate, setValidate] = useState<boolean>(false);
  const [formData, setFormData] = useState<StartJobFormDataType | null>(null);

  // Refs
  const contextRef = useRef<HTMLInputElement>(null);

  // Labels
  const labels = useMemo(() => {
    return {
      cancel: t(CONFIGURATION_STRINGS.cancel),
      context: t(CONFIGURATION_STRINGS.startJobContextLabel),
      errorUnknown: t(CONFIGURATION_STRINGS.errorUnknown),
      jobFailToastDescription2: t(CONFIGURATION_STRINGS.jobFailToastDescription2),
      jobFailToastTitle: t(CONFIGURATION_STRINGS.error),
      jobStartFailToastDescription1: t(CONFIGURATION_STRINGS.jobStartOrCancelFailToastDescription1),
      jobSuccessToastDescription1: t(CONFIGURATION_STRINGS.jobSuccessToastDescription1),
      jobSuccessToastDescription2: t(CONFIGURATION_STRINGS.jobSuccessToastDescription2),
      jobSuccessToastTitle: t(CONFIGURATION_STRINGS.jobSuccessToastTitle),
      requiredField: t(CONFIGURATION_STRINGS.requiredField),
      selectContext: t(CONFIGURATION_STRINGS.selectContext),
      specifyInputValues: t(CONFIGURATION_STRINGS.specifyInputValues),
      startJob: t(CONFIGURATION_STRINGS.startJob),
      startJobDialogTitle: t(CONFIGURATION_STRINGS.startJobDialogTitle),
    };
  }, [t]);

  // Determines if JSON Editor will be rendered
  const isInputRequired = jobManifest && jobManifest.entryFunction.input.properties ? true : false;

  const startJob = useCallback((): void => {
    // Prepare toast
    const successToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: labels.jobSuccessToastTitle,
      toastContent: (
        <span>
          {labels.jobSuccessToastDescription1}
          <i> {heading} </i>
          {labels.jobSuccessToastDescription2}
        </span>
      ),
      toastType: TOAST_TYPE_SUCCESS,
    };

    // Prepare DTO
    const dto: JobRunnerServiceBeginExecutionDto = {
      context: selectedContext as string,
      input: formData,
      jobName: heading,
    };

    lastValueFrom(jobRunnerService.beginJobExecution(dto))
      .then(() => {
        setSubmit(false);
        close();

        updateActiveToast(null);
        updateActiveToast(<Toast {...successToastProps} />);
        return;
      })
      .catch(() => {
        setSubmit(false);
        setFormError(labels.errorUnknown);
      });
  }, [close, formData, heading, jobRunnerService, labels, selectedContext, updateActiveToast]);

  const formSubmitHandler = useCallback(
    (errors: Array<ValidationError>): void => {
      if (!selectedContext) {
        setContextError(labels.requiredField);
        setSubmit(false);
        setValidate(false);
        return;
      } else if (errors.length > 0 && selectedContext) {
        setContextError('');
        setSubmit(false);
        setValidate(false);
        return;
      }

      setValidate(false);
      startJob();
    },
    [labels, selectedContext, startJob],
  );

  // Set up correct form buttons
  useEffect(() => {
    const cancelButton = (
      <Button
        dataId={START_JOB_EXECUTIONS_DATA_IDS.cancel}
        format={BUTTON_FORMAT_SOFT}
        onClick={close}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const submitButton = (
      <Button
        dataId={START_JOB_EXECUTIONS_DATA_IDS.startJob}
        format={BUTTON_FORMAT_STRONG}
        isLoading={isFormSubmitted}
        onClick={(): void => {
          setSubmit(true);
          // Clear form error if present
          setFormError(``);

          if (isInputRequired) {
            setValidate(true);
          } else {
            // Workaround to reuse submit handler for context validation only
            formSubmitHandler([]);
          }
        }}
        text={labels.startJob}
        theme={BUTTON_THEME_PRIMARY}
      />
    );

    if (jobManifest && contextList) {
      if (isFormSubmitted) {
        setAfterClose && setAfterClose(() => refreshData);
        setButtonKeys(['Start Job']);
        setButtons([submitButton]);
      } else {
        setButtonKeys(['Cancel', 'Start Job']);
        setButtons([cancelButton, submitButton]);
      }
    }
  }, [
    close,
    contextList,
    formSubmitHandler,
    isFormSubmitted,
    isInputRequired,
    jobManifest,
    labels,
    refreshData,
    setAfterClose,
    setButtonKeys,
    setButtons,
    startJob,
  ]);

  // Retrieve the correct job manifest
  useEffect(() => {
    if (!jobs) {
      return;
    }

    const matchingJobs = jobs.filter((job) => job.name === heading);
    const { version } = matchingJobs[0];

    const dto = {
      name: heading,
      version: version as string,
    };

    lastValueFrom(jobRunnerService.getJobManifest(dto))
      .then((response) => {
        setJobManifest(response);
        return;
      })
      .catch(() => {
        setJobManifest(null);
      });
  }, [heading, jobRunnerService, jobs]);

  if (!jobManifest || !contextList) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  // Set up context dropdown
  const contextOptions: Array<SelectFieldOption> = contextList.map((context) => {
    return {
      key: context.contextName,
      label: context.contextName,
      value: context.contextName,
    };
  });

  const contextSelectFieldProps: SelectFieldProps = {
    callback: (option: SelectFieldOption): void => {
      setSelectedContext(option.value);
    },
    errorMessage: contextError,
    fieldLabel: labels.context,
    id: JOB_EXECUTIONS_DATA_IDS.context,
    name: JOB_EXECUTIONS_DATA_IDS.context,
    options: contextOptions,
    placeholder: labels.selectContext,
    requiresCustomPosition: true,
    value: selectedContext,
  };

  // Set up JSON Editor implementation
  const jobInputSchema: JobManifestInputSchema = {
    ...jobManifest.entryFunction.input,
    title: labels.specifyInputValues,
  };

  const editorProps: JsonEditorProps = {
    data: formData as StartJobFormDataType,
    onValidate: formSubmitHandler,
    schema: jobInputSchema as Schema,
    setData: setFormData as JsonEditorProps['setData'],
    showDescriptions: false,
    showHeader: false,
    showSubmitButton: false,
    validate: validate,
  };

  return (
    <div className="start-job-dialog-container">
      <span>{labels.startJobDialogTitle}</span>
      <div className="context-select-field">
        <SelectField {...contextSelectFieldProps} data-id={JOB_EXECUTIONS_DATA_IDS.context} ref={contextRef} />
      </div>
      {isInputRequired && (
        <div className="start-job-form__json-editor">
          <JsonEditor {...editorProps} />
        </div>
      )}
      {formError && (
        <div className="sds-form-error" data-id={START_JOB_EXECUTIONS_DATA_IDS.startJobError}>
          <ErrorMessage message={formError} />
        </div>
      )}
    </div>
  );
};
